import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { SubscriptionService } from 'utility/services/subscription'
import {
    ACCOUNT_CHECKOUT_PATH,
    ACCOUNT_PRICING_PATH,
    ENABLE_LICENSE_SHOPPING_CART,
    isPaymentStatusPaid,
} from 'utility/subscription-usage'
import { CancelPlanModal } from './CancelPlanModal'
import { PendingPaymentBanner } from './PendingPaymentBanner'

const Card = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 16px;
`

const Hd = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 8px;
`

const Title = styled.div`
    font-size: 15px;
    font-weight: 700;
    color: #111827;
`

const Sub = styled.div`
    font-size: 13px;
    color: #6b7280;
    margin-top: 4px;
`

const Badge = styled.span`
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    &.free {
        background: #e0e7ff;
        color: #3730a3;
    }
    &.paid {
        background: #d1fae5;
        color: #065f46;
    }
`

const Actions = styled.div`
    margin-top: 16px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`

const Silent = styled.div`
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 16px;
`

const CANCEL_NOTE_KEY = 'eauditor.subscriptionCancelled'

const truthy = value =>
    value === true || value === 1 || value === '1' || value === 'true'

const readField = (sources, keys) => {
    let found
    sources.forEach(source => {
        if (!source || found !== undefined) return
        keys.forEach(key => {
            if (found !== undefined) return
            const value = source[key]
            if (value !== undefined && value !== null && value !== '') {
                found = value
            }
        })
    })
    return found
}

const planSources = status => {
    if (!status || typeof status !== 'object') return []
    const nestedRaw = status.ServicePlan ?? status.servicePlan
    const nested = nestedRaw && typeof nestedRaw === 'object' ? nestedRaw : null
    return [nested, status].filter(Boolean)
}

const statusSaysCancelled = status => {
    const sources = planSources(status)
    if (!sources.length) return false
    const flag = readField(sources, [
        'Cancelled',
        'IsCancelled',
        'AlreadyCancelled',
        'IsSubscriptionCancelled',
        'SubscriptionCancelled',
        'CancelAtPeriodEnd',
        'IsCancelAtPeriodEnd',
        'PlanCancelled',
        'IsPlanCancelled',
    ])
    if (truthy(flag)) return true
    const label = String(
        readField(sources, ['SubscriptionStatus', 'PlanStatus']) || '',
    ).toLowerCase()
    return (
        label === 'cancelled' ||
        label === 'canceled' ||
        label === 'cancelling' ||
        label === 'cancelpending'
    )
}

const subscriptionRenewedPastCancel = (status, note) => {
    const statusEnd = new Date(
        readField(planSources(status), ['PlanEndingOn', 'planEndingOn']) || '',
    ).getTime()
    const noteEnd = new Date(note?.activeUntil || '').getTime()
    if (
        !Number.isNaN(statusEnd) &&
        !Number.isNaN(noteEnd) &&
        statusEnd > noteEnd + 24 * 60 * 60 * 1000
    ) {
        return true
    }
    return false
}

const readCancelNote = () => {
    try {
        const raw = sessionStorage.getItem(CANCEL_NOTE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const rememberCancelNote = payload => {
    const activeUntil = payload?.ActiveUntil || payload?.activeUntil || ''
    try {
        sessionStorage.setItem(
            CANCEL_NOTE_KEY,
            JSON.stringify({ activeUntil, at: Date.now() }),
        )
    } catch {
        // The billing card still updates for this visit.
    }
}

const clearCancelNote = () => {
    try {
        sessionStorage.removeItem(CANCEL_NOTE_KEY)
    } catch {
        // Ignore storage failures.
    }
}

const formatEnding = value => {
    if (!value) return null
    try {
        return new Date(value).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })
    } catch {
        return null
    }
}

/**
 * Account & Billing block (mockup Step 1 / 6).
 * Layered above Free-plan PlanUsagePanel. Fail-open if PaymentStatus missing.
 *
 * @param {function} [onBillingChanged] refresh usage after pay/cancel
 * @param {function} [onPaidStatusChange] (boolean|null) IsPaidMember for usage UI
 */
export const ServicePlanBillingCard = ({
    onBillingChanged,
    onPaidStatusChange,
}) => {
    const [status, setStatus] = useState(null)
    const [loadError, setLoadError] = useState(false)
    const [cancelOpen, setCancelOpen] = useState(false)
    const [cancelNote, setCancelNote] = useState(() => readCancelNote())

    const refresh = useCallback(async () => {
        try {
            const data = await SubscriptionService.getPaymentStatus()
            setStatus(data)
            setLoadError(false)
            const noted = readCancelNote()
            if (noted && subscriptionRenewedPastCancel(data, noted)) {
                clearCancelNote()
                setCancelNote(null)
            }
        } catch {
            setLoadError(true)
            setStatus(null)
        }
    }, [])

    useEffect(() => {
        refresh()
    }, [refresh])

    // PaymentStatus sometimes returns an unrelated user-shaped payload.
    // Only trust real billing fields (mockup Step 1 shape).
    const billing = (() => {
        if (!status || typeof status !== 'object') return null
        const nestedRaw = status.ServicePlan ?? status.servicePlan
        const nested =
            nestedRaw && typeof nestedRaw === 'object' ? nestedRaw : null
        const hasPlanFields =
            status.PlanId != null ||
            status.planId != null ||
            status.PlanName != null ||
            status.planName != null ||
            status.IsPaidMember != null ||
            status.isPaidMember != null ||
            nested
        if (!hasPlanFields) return null
        const rawName =
            nested?.PlanDescription ||
            nested?.planDescription ||
            nested?.PlanName ||
            nested?.planName ||
            status.PlanName ||
            status.planName ||
            ''
        return {
            planId:
                status.PlanId ??
                status.planId ??
                nested?.PlanId ??
                nested?.planId,
            planName: String(rawName).trim() || null,
            isPaidMember: isPaymentStatusPaid(status),
            isAutoRenewal:
                status.IsAutoRenewal ??
                status.isAutoRenewal ??
                nested?.IsAutoRenewal ??
                nested?.isAutoRenewal,
            planEndingOn:
                status.PlanEndingOn ||
                status.planEndingOn ||
                nested?.PlanEndingOn ||
                nested?.planEndingOn,
            cancelledOn:
                status.CancelledOn ||
                status.cancelledOn ||
                status.PlanCancelledOn ||
                status.planCancelledOn,
            cancelledPlanName:
                status.CancelledPlanName ||
                status.cancelledPlanName ||
                status.PreviousPlanName ||
                status.previousPlanName ||
                '',
        }
    })()

    const planName = billing?.planName || '—'
    // Mockup: IsPaidMember drives paid UI. Do NOT infer paid from Usage alone —
    // PurchaseOrder can leave Usage/status inconsistent before PaymentConfirm.
    const isPaid = billing?.isPaidMember === true || billing?.isPaidMember === 1
    const showBillingUpgrade = !isPaid
    const autoRenew = Boolean(billing?.isAutoRenewal)
    const ending = formatEnding(billing?.planEndingOn)
    const cancelledOn = formatEnding(billing?.cancelledOn)
    const cancelledPlanName = billing?.cancelledPlanName || ''

    // Tell Account page whether payment is confirmed so Plan Usage does not
    // show Yearly/Unlimited from a premature Usage flip after PurchaseOrder.
    useEffect(() => {
        if (typeof onPaidStatusChange !== 'function') return
        if (loadError) {
            onPaidStatusChange(null)
            return
        }
        if (!status) return
        onPaidStatusChange(isPaid)
    }, [isPaid, status, loadError, onPaidStatusChange])

    const navigate = useNavigate()
    const openUpgrade = () => {
        if (!ENABLE_LICENSE_SHOPPING_CART) return
        navigate(ACCOUNT_CHECKOUT_PATH)
    }

    const handleCancelled = async data => {
        const payload = data && typeof data === 'object' ? data : null
        const endedNow =
            payload?.Immediate === true ||
            payload?.AlreadyFree === true ||
            payload?.alreadyFree === true
        const cancelled =
            payload?.Cancelled === true || payload?.AlreadyCancelled === true
        if (payload && !endedNow && cancelled) {
            rememberCancelNote(payload)
            setCancelNote(readCancelNote())
        }
        if (endedNow) {
            clearCancelNote()
            setCancelNote(null)
        }
        await refresh()
        onBillingChanged?.()
    }

    if (loadError && !status) {
        return (
            <>
                <Silent>
                    Billing status API not available yet. Free-plan usage below
                    still works from Subscription/Usage.
                </Silent>
                <Card>
                    <Hd>
                        <div>
                            <Title>Account &amp; Billing</Title>
                            <Sub>Your subscription and usage</Sub>
                        </div>
                    </Hd>
                    <Actions>
                        {ENABLE_LICENSE_SHOPPING_CART && (
                            <AppPrimaryButton type="button" onClick={openUpgrade}>
                                Upgrade plan
                            </AppPrimaryButton>
                        )}
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate(ACCOUNT_PRICING_PATH)}
                        >
                            See other plans
                        </AppSecondaryButton>
                    </Actions>
                </Card>
                {ENABLE_LICENSE_SHOPPING_CART && (
                    <PendingPaymentBanner onRestartUpgrade={openUpgrade} />
                )}
            </>
        )
    }

    if (!status) return null

    const planTitle = /plan$/i.test(planName) ? planName : `${planName} plan`
    const cancelStillActive =
        isPaid && (statusSaysCancelled(status) || Boolean(cancelNote))
    const validUntil =
        formatEnding(cancelNote?.activeUntil) || ending
    let paidSubtitle = planTitle
    if (cancelStillActive && validUntil) {
        paidSubtitle = `${planTitle} · Valid until ${validUntil}`
    } else if (ending) {
        paidSubtitle = `${planTitle} · Renews on ${ending}`
    }
    if (!cancelStillActive && autoRenew) {
        paidSubtitle = `${paidSubtitle} · Auto-renewal on`
    }
    const cancelledSubtitle = cancelledOn
        ? `You cancelled${
              cancelledPlanName ? ` ${cancelledPlanName}` : ''
          } on ${cancelledOn}`
        : 'Your subscription and usage'
    const billingSubtitle = isPaid ? paidSubtitle : cancelledSubtitle

    let billingActions = null
    if (isPaid) {
        billingActions = (
            <>
                {ENABLE_LICENSE_SHOPPING_CART && (
                    <AppSecondaryButton type="button" onClick={openUpgrade}>
                        {cancelStillActive ? 'Renew plan' : 'Change plan'}
                    </AppSecondaryButton>
                )}
                <AppSecondaryButton
                    type="button"
                    onClick={() => setCancelOpen(true)}
                    disabled={cancelStillActive}
                >
                    Cancel plan
                </AppSecondaryButton>
            </>
        )
    } else if (showBillingUpgrade) {
        billingActions = (
            <>
                {ENABLE_LICENSE_SHOPPING_CART && (
                    <AppPrimaryButton type="button" onClick={openUpgrade}>
                        Upgrade plan
                    </AppPrimaryButton>
                )}
                <AppSecondaryButton
                    type="button"
                    onClick={() => navigate(ACCOUNT_PRICING_PATH)}
                >
                    See other plans
                </AppSecondaryButton>
            </>
        )
    }

    return (
        <>
            {ENABLE_LICENSE_SHOPPING_CART && (
                <PendingPaymentBanner onRestartUpgrade={openUpgrade} />
            )}
            <Card>
                <Hd>
                    <div>
                        <Title>Account &amp; Billing</Title>
                        <Sub>{billingSubtitle}</Sub>
                    </div>
                    <Badge className={isPaid ? 'paid' : 'free'}>
                        {(() => {
                            if (isPaid) return 'Active'
                            if (planName && planName !== '—') return planName
                            return 'Free'
                        })()}
                    </Badge>
                </Hd>
                <Actions>{billingActions}</Actions>
            </Card>

            <CancelPlanModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                onCancelled={handleCancelled}
            />
        </>
    )
}
